import api from "@/_helpers/api";
import { defineStore } from "pinia";
import type { OpenSearchCursor, Pagination } from "@/types";
import { axiosInstance } from "@/plugins/https";
import { FilterContext, useFiltersStore } from "@/_store/filters.module";
import { handleVirtualScrollData } from "@coronet/cui";
import { parseOpenSearchQuery } from "@/_helpers/pagination";
import { useSnackbarStore } from "@/_store/snackbar.module";
import { i18n } from "@/plugins/i18n";
import { SubscriptionModule } from "@/constants/workplaces";
import type { AxiosResponse } from "axios";
import { type UserBaseListItem, type UserDetails } from "../users.module.ts";
import { MspUserVulnerabilityStatus } from "@/constants/msp-users.ts";
import cloneDeep from "lodash/cloneDeep";
import { firstPageQueryEntityActivityLogs } from "@/constants/general.ts";
import { isMspAccessRestricted } from "@/_helpers/msp-permissions.ts";
import { StoreModule } from "@/constants/store.ts";
import {
  UsersSuspendedFilterItem,
  ProtectedUsersAction,
  UsersMfaFiltersItem,
} from "@/constants/users.ts";
import { RolePermissionsScope, WorkspaceManagementScopeSections } from "@/constants/roles.ts";

export interface MspUserDetails extends Omit<UserDetails, "labels"> {
  workspaceId: string;
  workspaceName: string;
}

export interface MspUserListItem extends UserBaseListItem {
  workspaceId: string;
  workspaceName: string;
}

interface MspUsersState {
  loading: boolean;
  items: Array<MspUserListItem>;
  total: number;
  pagination: Pagination;
  usersLoading: boolean;
  search: null | string;
  userDetailsLoading: boolean;
  activityLogsLoading: boolean;
  userDetails: MspUserDetails;
  cursor: OpenSearchCursor;
}

const defaultMspUsersState: MspUsersState = {
  loading: false,
  items: [],
  total: 0,
  pagination: {
    page: 0,
    pageSize: 25,
  },
  usersLoading: true,
  search: null,
  userDetailsLoading: false,
  activityLogsLoading: false,
  userDetails: {
    id: null,
    name: null,
    protectionGrantedTime: 0,
    cloudServices: [],
    tickets: [],
    activityLogs: [],
    aliases: [],
    associatedDevices: [],
    workspaceId: "",
    workspaceName: "",
    suspended: false,
    autoForwardingBlockingPolicySelected: false,
  },
  cursor: {
    nextPage: firstPageQueryEntityActivityLogs,
  },
};

export const useMspUsersStore = defineStore(StoreModule.MSP_USERS, {
  state: (): MspUsersState => cloneDeep({ ...defaultMspUsersState }),
  actions: {
    async getUserDetails({ id, workspaceId, workspaceName }: MspUserListItem): Promise<void> {
      this.userDetailsLoading = true;

      try {
        const activityLogsAccessRestricted = isMspAccessRestricted(
          RolePermissionsScope.WORKSPACE_MANAGEMENT,
          WorkspaceManagementScopeSections.ACTIVITY_LOGS
        );

        const ticketsAccessRestricted = (
          [
            SubscriptionModule.USER_DATA_GOVERNANCE,
            SubscriptionModule.CLOUD_SECURITY,
            SubscriptionModule.EMAIL_SECURITY,
          ] as const
        ).every((module) => isMspAccessRestricted(RolePermissionsScope.TICKETS, module));

        const userInfoPromise = axiosInstance.request({
          ...api.getMspUserInfo(id),
          params: { workspaceId },
        });
        const userAssociatedDevicesPromise = axiosInstance.request({
          ...api.getMspUserAssociatedDevices(id),
          params: { workspaceId },
        });

        const userTicketsPromise = ticketsAccessRestricted
          ? Promise.resolve({ data: [] }) // Fallback promise
          : axiosInstance.request({
              ...api.getMspUserTickets(id),
              params: { workspaceId },
            });

        const userActivityLogsPromise = activityLogsAccessRestricted
          ? Promise.resolve({ data: { items: [], cursor: null } }) // Fallback promise
          : axiosInstance.request({
              ...api.getMspUserActivityLogs(id),
              params: {
                ...parseOpenSearchQuery(firstPageQueryEntityActivityLogs),
                workspaceId,
              },
            });

        const [
          userInfoResponse,
          userTicketsResponse,
          userActivityLogsResponse,
          userAssociatedDevicesResponse,
        ] = await Promise.all([
          userInfoPromise,
          userTicketsPromise,
          userActivityLogsPromise,
          userAssociatedDevicesPromise,
        ]);

        this.cursor = userActivityLogsResponse.data.cursor;
        this.userDetails = {
          ...userInfoResponse.data,
          tickets: userTicketsResponse.data,
          activityLogs: userActivityLogsResponse.data.items,
          associatedDevices: userAssociatedDevicesResponse.data,
          workspaceId,
          workspaceName,
        };
      } catch (error) {
        console.error("Failed to get user details:", error);
      } finally {
        this.userDetailsLoading = false;
      }
    },
    async getItems(showSkeletonLoader: boolean = false) {
      this.loading = true;
      this.usersLoading = showSkeletonLoader;
      const filtersStore = useFiltersStore();
      const {
        search,
        workspaceIds,
        suspended,
        hasVulnerabilities,
        cloudServices,
        mfaFilters = [],
      } = filtersStore.filters[FilterContext.MSP_USERS];

      const request = {
        ...api.getMspUsers(),
        data: {
          hasVulnerabilities:
            hasVulnerabilities === MspUserVulnerabilityStatus.ALL
              ? null
              : hasVulnerabilities === MspUserVulnerabilityStatus.WITH_ISSUES,
          search,
          workspaceIds: workspaceIds ?? [],
          suspended: suspended ? suspended === UsersSuspendedFilterItem.SUSPENDED : null,
          cloudServices: cloudServices ?? [],
          mfaEnabled: mfaFilters.includes(UsersMfaFiltersItem.MFA_ENABLED),
          mfaDisabled: mfaFilters.includes(UsersMfaFiltersItem.MFA_DISABLED),
        },
        params: {
          ...this.pagination,
        },
      };

      try {
        const { data } = await axiosInstance.request(request);
        this.items = data.items;
        this.total = data.total;
      } catch (error) {
        console.error(error);
      }

      this.loading = false;
      this.usersLoading = false;
    },
    async onPageChange(pagination: Pagination) {
      this.pagination = pagination;
      await this.getItems();
    },

    async undoActivityLog(id: string) {
      this.activityLogsLoading = true;

      try {
        const request = {
          ...api.activityLogsUndo(id),
          headers: {
            Workspace: this.userDetails.workspaceId,
          },
        };

        await axiosInstance.request(request);

        this.resetCursor();

        const userActivityLogsRequest = {
          ...api.getMspUserActivityLogs(this.userDetails.id as string),
          params: {
            ...parseOpenSearchQuery(this.cursor.nextPage!),
            workspaceId: this.userDetails.workspaceId,
          },
        };

        const { data } = await axiosInstance.request(userActivityLogsRequest);
        this.cursor = data.cursor;
        this.userDetails.activityLogs = data.items;
      } catch (error) {
        console.error(error);
      }

      this.activityLogsLoading = false;
    },
    async applySingleUserAction(
      userId: string,
      {
        action,
        metadata,
      }: {
        action: ProtectedUsersAction;
        metadata?: Record<string, unknown>;
      }
    ) {
      const request = {
        ...api.mspSingleUserAction(userId),
        data: {
          action,
          metadata,
        },
      };

      try {
        await axiosInstance.request(request);
        useSnackbarStore().addGenericSuccess(i18n.global.t(`snackbar.messages.users.${action}`));
        await this.getItems();
      } catch (e) {
        console.error(e);
      }
    },
    async applyBulkUserAction({
      action,
      metadata,
      selection,
    }: {
      action: ProtectedUsersAction;
      selection: MspUserListItem[];
      metadata?: Record<string, unknown>;
    }) {
      const request = {
        ...api.mspUsersBulkAction,
        data: {
          action,
          userIds: selection.map((v) => v.id),
          metadata,
        },
      };

      try {
        await axiosInstance.request(request);
        useSnackbarStore().addGenericSuccess(i18n.global.t(`snackbar.messages.users.${action}`));
        await this.getItems();
      } catch (e) {
        console.error(e);
      }
    },
    getSingleUserActions(
      userId: string
    ): Promise<AxiosResponse<{ items: ProtectedUsersAction[] }>> {
      const request = {
        ...api.mspSingleUserActionsList(userId),
      };

      return axiosInstance.request(request);
    },
    getUsersBulkActions(
      userIds: string[]
    ): Promise<AxiosResponse<{ items: ProtectedUsersAction[] }>> {
      const request = {
        ...api.mspUsersAvailableBulkActions,
        data: {
          userIds,
        },
      };

      return axiosInstance.request(request);
    },
    async loadMoreMspUserActivityLogs() {
      this.activityLogsLoading = true;

      try {
        const request = {
          ...api.getMspUserActivityLogs(this.userDetails.id!),
          params: {
            ...parseOpenSearchQuery(this.cursor.nextPage!),
            workspaceId: this.userDetails.workspaceId,
          },
        };

        const { data } = await axiosInstance.request(request);
        this.cursor = data.cursor;
        this.userDetails = {
          ...this.userDetails,
          activityLogs: handleVirtualScrollData(this.userDetails.activityLogs, data.items, "id"),
        };

        this.activityLogsLoading = false;
      } catch (error) {
        console.error(error);
      }

      this.activityLogsLoading = false;
    },
    resetCursor() {
      this.cursor = {
        nextPage: firstPageQueryEntityActivityLogs,
      };
    },
  },
});
